import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { CircularProgress, Box, Typography } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
  requiredPermission?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole, 
  requiredPermission 
}) => {
  const { state } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication (with timeout)
  if (state.loading) {
    return (
      <Box 
        display="flex" 
        flexDirection="column"
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // Redirect to home page if not authenticated
  if (!state.isAuthenticated || !state.user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Check role-based access (if required)
  if (requiredRole && state.user) {
    const roleId = state.user.roleId ?? (state.user as any)?.role_id ?? (state.user as any)?.role?.id;
    const roleName = String((state.user as any)?.role?.name ?? (state.user as any)?.role ?? '').toLowerCase().trim();
    const isAdmin = roleId === 1 || roleName === 'admin' || roleName === 'super_admin' || roleName === 'superadmin';

    if (requiredRole === 'admin' && !isAdmin) {
      return <Navigate to="/app/unauthorized" replace />;
    }
  }

  // Check permission-based access (if required)
  if (requiredPermission) {
    // This would need to be implemented based on your permission system
    // For now, we'll skip this check
  }

  return <>{children}</>;
};

export default ProtectedRoute;